<template>
    <div class="rlms-container privacy-page">
        <div class="content">
            <h1>Data Makes the Difference</h1>
            <p>
                <strong>Effective Date:</strong> {{ doc.effective }}
                &nbsp;·&nbsp; <strong>Version:</strong> {{ doc.version }}
            </p>

            <section>
                <p class="rlms-mb-5">
                    Thank you for your interest in <strong>Data Makes the Difference, LLC</strong>. We have prepared
                    this Privacy Policy to explain how we collect, use, retain, and share information obtained through
                    your use of datamtd.com, eflapp.com, vbmappapp.com, and eesappapp.com (the "Site") or when you
                    contact us. This Privacy Policy also explains how to opt out of certain communications, request
                    disclosure or deletion of your Personal Information (as defined below), and exercise your data
                    rights. If you do not agree to the terms of this Privacy Policy, please do not use or access the
                    Site.
                </p>
                <p>
                    Data Makes the Difference, LLC, located at 17 Greystone Drive, Mountain Top, Pennsylvania, USA, is
                    the controller of and responsible for your personal data (collectively referred to as "we", "us" or
                    "our"). Our contact information appears below.
                </p>
            </section>

            <section>
                <h2>Information We Collect</h2>
                <p class="rlms-mb-5"><strong>Information Collection:</strong><br>
                    When you request services, we may ask for Personal Information such as your name, email address,
                    password, and phone number.<br>
                    Additionally, when adding Learners, we may collect information including their name, date of birth,
                    gender, language, the names of their parents or guardians if you enter them, an optional
                    photograph, and optionally, special categories of data such as diagnosis or medications.<br>
                    In EESAPP we also hold the learner's assessment results and, where a clinician chooses to record
                    one, recordings of the learner's speech together with the transcript and the marks made from it.
                    The section "Voice recordings, speech analysis and AI" below explains those in full.
                </p>
                <p>
                    We automatically collect Traffic Data when you visit the Site, including your IP address, browser
                    type, pages viewed, and links clicked. We use cookies and web beacons to enhance your browsing
                    experience.
                </p>

                <p class="rlms-mt-5 rlms-mb-5"><strong>Use of Information:</strong><br>
                    We use your Personal Information to create user accounts, process purchases, and provide support.
                    For Learners, we use this information to deliver assessment and learning services and generate
                    progress reports. Traffic Data helps us monitor site usage and improve user experience.
                </p>

                <p><strong>Information Sharing:</strong><br>
                    We do not sell Personal Information. However, we may share it with trusted service providers who
                    help us operate our platform and services. These providers are contractually bound to protect your
                    data. We may also disclose information in response to legal requests or to protect our legal rights.
                </p>
            </section>
            <section>
                <h2>Voice recordings, speech analysis and AI</h2>

                <p class="rlms-mb-5"><strong>Why we record.</strong> EESAPP is an assessment of how a child repeats
                    sounds and words. A clinician can record the child saying a test item so they can listen again,
                    mark the sounds they heard, and get a second opinion from our speech-analysis software.</p>

                <p class="rlms-mb-5"><strong>Recording is always a choice.</strong> Nothing is recorded unless the
                    clinician presses Record, and nothing is uploaded to us unless they then press Save. No part of the
                    assessment requires a recording. A recording is at most about half a minute long and covers one
                    test item. Because the clinician says the word first and the child repeats it, a recording usually
                    contains the clinician's voice as well as the child's.</p>

                <p class="rlms-mb-5"><strong>Parental consent is the organization's responsibility.</strong> The
                    organization using EESAPP — the clinic, practice, school or independent therapist — decides whether
                    to record a child and must have the permission of the child's parent or guardian, plus any
                    permission their employer, school district or state law requires, before they record. They hold
                    that permission on file. We do not collect it and cannot see it. If permission is withdrawn, the
                    organization must stop recording and can delete any recording already made.</p>

                <p class="rlms-mb-5"><strong>Where recordings are stored.</strong> A recording is sent to us over an
                    encrypted connection and stored in our own storage in Amazon Web Services, in a private location
                    that is encrypted and closed to the public internet. It is never published, never given a public
                    link, and never sent to an advertiser or a data broker.</p>

                <p class="rlms-mb-5"><strong>Who can listen to a recording.</strong> Only the organization that made it
                    — its owner, the administrators it has appointed, and staff it has given permission for that
                    particular child — and members of our own support team who hold an administrator account. Anyone
                    else asking for a recording is told it does not exist. We do not sell recordings, share them with
                    other customers, or use them to build a product for anyone else.</p>

                <p class="rlms-mb-5"><strong>How the analysis works, and who performs it.</strong> If the organization
                    has agreed to our AI terms and the clinician has automatic transcription switched on, the part of
                    the clip the clinician selected is analysed by two pieces of software, both run by us inside our
                    own Amazon Web Services account:</p>
                <ul class="rlms-mb-5">
                    <li>a speech-to-text service, which writes down what it hears;</li>
                    <li>for the single-syllable, word and blend groups, our own phoneme recognizer, which reports the
                        individual speech sounds it hears. It runs on a server we operate and keeps nothing: the audio
                        is deleted as soon as the answer is returned.</li>
                </ul>

                <p class="rlms-mb-5"><strong>No AI company receives a recording.</strong> No recording and no part of a
                    recording is sent to OpenAI, Google, Anthropic or any other AI provider.</p>

                <p class="rlms-mb-5"><strong>What the analysis is used for.</strong> It produces a transcript, a mark
                    for each expected sound, and sometimes a suggested score. It is help, not judgement: the clinician
                    decides every score, the software never lowers a score, and a clinician's own score is never
                    overwritten. The clinician can tell us the analysis was wrong, which we use to measure how accurate
                    the software is.</p>

                <p class="rlms-mb-5"><strong>Where we use Claude.</strong> For the planning features — suggesting
                    practice words and breaking words into steps — we use Claude, an AI model from Anthropic, which we
                    run inside our own Amazon Web Services account using Amazon Bedrock. It receives sound counts, word
                    lists, phonetic spellings and whatever the clinician typed into the preference boxes.
                    <strong>It does not receive any recording, any child's record, or any name, date of birth or other
                        identifier from our database.</strong> Amazon Bedrock does not keep these requests to train
                    models, and the data stays inside our account. Clinicians: the preference boxes are free text, and
                    whatever you type is sent. Do not type a child's name, a sibling's name, a school or anything else
                    that identifies them.
                </p>

                <p class="rlms-mb-5"><strong>We do not use recordings to train AI.</strong> We do not use children's
                    recordings, transcripts or scores to train or improve any AI model, our own or anyone else's. If we
                    ever want to, two things will be true before we do: the organization must switch on a separate
                    setting called "Allow de-identified recordings to help improve the AI", which is off until an
                    administrator deliberately turns it on and can be turned off again at any time; and we will update
                    this policy and tell you before any such use starts. Switching that setting on will never be a
                    condition of using EESAPP, and the assessment works exactly the same with it off.</p>

                <p class="rlms-mb-5"><strong>What we keep, and for how long.</strong> For an assessment item we keep
                    the recording, the transcript made from it, the mark against each sound and the score. We keep
                    them to provide the service: so the clinician can listen again, check the marking, and compare a
                    child's progress over time. <strong>Nothing is deleted because of its age.</strong> A recording
                    stays until you delete it: a clinician or an administrator can delete a recording — or re-record
                    it — delete an assessment, delete a child's record, or close the account, and everything held
                    under what is deleted goes with it. When that happens we remove the recording from live storage
                    straight away and purge any stored older copy of it within 30 days; the transcript, the marks and
                    the score are removed from our live systems straight away and from our backups within a reasonable
                    period, normally 30 days. Deleting a recording on its own does not delete the transcript or the
                    marks made from it: those are part of the assessment result and stay with the assessment until the
                    assessment itself is deleted.</p>

                <p><strong>Your choices.</strong> An organization can decline our AI terms, in which case no recording
                    is ever analysed automatically. Each clinician can switch automatic transcription off for their own
                    work. Any recording can be deleted from the app at any time. And you can ask us to delete all
                    recordings for a child, or for your whole organization, by writing to
                    <a href="mailto:privacy@datamtd.com" class="rlms-contents">privacy@datamtd.com</a>.</p>
            </section>

            <section>
                <h2>Service Providers</h2>
                <p class="rlms-mb-5">
                    We work with trusted third-party service providers to deliver and support our services. These
                    providers may process Personal Information solely for the purpose of performing services on our
                    behalf and are contractually required to maintain confidentiality and security.
                </p>

                <div>
                    Examples of service providers include:
                    <ul>
                        <li><strong>Amazon Web Services (AWS)</strong> – hosting, databases, file storage, the
                            speech-to-text service and, through Amazon Bedrock, the Claude model used for the planning
                            features. All learner data, including recordings, is held here.</li>
                        <li><strong>Anthropic</strong> – maker of the Claude model. The model runs inside our own
                            Amazon Web Services account; Anthropic does not receive our requests.</li>
                        <li><strong>Stripe</strong> – for secure payment processing. Receives billing details, not
                            learner data.</li>
                        <li><strong>Intercom</strong> – support chat. Receives your own name and email address, not
                            learner data.</li>
                        <li><strong>Vanta</strong> – for continuous security and compliance monitoring.</li>
                    </ul>
                    <p class="rlms-mt-5">A complete and current list of our subprocessors is available upon request.</p>
                </div>
            </section>

            <section>
                <h2>Data Security and Compliance</h2>
                <p class="rlms-mb-5">
                    We are committed to protecting your data and complying with the Health Insurance Portability and
                    Accountability Act (HIPAA). Our databases and file storage are hosted on Amazon Web Services (AWS)
                    and are encrypted, and all traffic to and from the app is encrypted. Account-holders' own contact
                    details are additionally encrypted field by field inside the database. We also undergo regular
                    third-party security and compliance audits, including HITRUST assessments.
                </p>
                <p>You can view our current compliance posture and security certifications at <a
                        href="https://trust.datamtd.com" style="display: contents;">https://trust.datamtd.com</a>.</p>
            </section>

            <section>
                <h2>Your Rights</h2>
                <p>
                    You have the right to opt out of marketing communications, request disclosure or deletion of your
                    Personal Information, and request a copy or transfer of your data. We do not discriminate against
                    individuals for exercising their data rights.
                </p>
            </section>

            <section>
                <h2>Data Retention</h2>
                <p>
                    We retain Personal Information for as long as necessary to fulfill the purposes outlined in this
                    policy unless a longer retention period is required by law. Data deleted by users is permanently
                    removed from active systems within 30 days and purged from backups in accordance with our retention
                    schedules. Voice recordings follow the same rule: once deleted they are removed within 30 days.
                    No recording is deleted because of its age — we keep it until you delete it or close your
                    account.
                </p>
            </section>

            <section>
                <h2>Links to Other Websites</h2>
                <p>
                    The Site may contain links to other websites with different privacy practices. We are not
                    responsible for the content or privacy practices of these sites and encourage you to review their
                    policies.
                </p>
            </section>

            <section>
                <h2>Children's data</h2>
                <p class="rlms-mb-5">
                    EESAPP is used by professionals to assess children. We therefore hold information about children on
                    purpose: their name, date of birth, gender, language, an optional photograph, the parents' names if
                    the organization enters them, their assessment results, and any recordings the clinician made.
                </p>
                <p class="rlms-mb-5">
                    Children do not have accounts and cannot sign in. Accounts are for professionals aged 18 or over.
                </p>
                <p class="rlms-mb-5">
                    We hold a child's information <strong>on behalf of the organization that entered it</strong> — the
                    clinic, practice, school or therapist. That organization decides what is collected and why, is
                    responsible for having the right permission from the child's parent or guardian, and is responsible
                    for answering a parent who asks what is held, for a copy, or for deletion. We act on that
                    organization's instructions and use the information only to provide the service to them.
                </p>
                <p class="rlms-mb-5">
                    We never use a child's information to advertise anything, to anyone. We do not sell it, we do not
                    share it with other customers, and we do not build profiles of children for any purpose other than
                    the assessment the clinician is carrying out.
                </p>
                <p>
                    A parent or guardian who wants to know what is held about their child, or wants it deleted, should
                    ask the organization that assessed them, who can do both inside EESAPP. They may also write to
                    <a href="mailto:privacy@datamtd.com" class="rlms-contents">privacy@datamtd.com</a> and we will help
                    the organization answer.
                </p>
            </section>

            <section>
                <h2>Security Measures</h2>
                <p>
                    We implement technical and organizational measures to protect your Personal Information from
                    unauthorized access or disclosure. However, due to the nature of the internet, we cannot guarantee
                    absolute security.
                </p>
            </section>

            <section>
                <h2>Changes to Policy</h2>
                <p>
                    We may update this Privacy Policy periodically. When we do, we will revise the “Effective Date”
                    above. Continued use of the Site after changes means you accept the revised terms.
                </p>
            </section>

            <section>
                <hr />
                <p class="rlms-mt-5">
                    For questions or concerns about this Privacy Policy, please contact us at
                    <a href="mailto:privacy@datamtd.com" class="rlms-contents">privacy@datamtd.com</a> or
                    <a href="tel:+15705504013" class="rlms-contents rlms-text-links-base">+1 (570) 550-4013</a>.
                </p>
            </section>
        </div>
    </div>
</template>

<script setup>
// The words are here; their version is in src/content/legal so the text and the
// version we record someone accepting cannot drift apart.
import { LEGAL_DOCUMENTS } from '@/content/legal';

// Not named `document`: that would shadow the DOM global inside this component.
const doc = LEGAL_DOCUMENTS.privacy;
</script>